import FlagProvider, { useUnleashContext } from '@unleash/proxy-client-react';
import React, { PropsWithChildren, useEffect } from 'react';
import { useSelector } from 'react-redux';

import useGetUuid from 'components/accountSettings/utils';

import { selectUserEmail } from 'state/slices/userSlice';

const APP_NAME = 's4c-dashboard-frontend';

function getUnleashConfig() {
  const unleash = window.s4cConfig?.unleash;
  if (!unleash?.url || !unleash?.clientKey) {
    return undefined;
  }
  return {
    url: unleash.url,
    clientKey: unleash.clientKey,
    appName: APP_NAME,
    refreshInterval: unleash.refreshInterval,
  };
}

export const UnleashProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const config = getUnleashConfig();

  return (
    <FlagProvider config={config} startClient={Boolean(config)}>
      {config && <UnleashAccountContext />}
      {children}
    </FlagProvider>
  );
};

/**
 * Account-scoped flags (e.g. multi-key-api) are evaluated backend-side on
 * the custom `accountUuid` Unleash context field — FeatureFlagsService.
 * isEnabled() sends `{ properties: { accountUuid } }`. Mirror that here (the
 * proxy client's context type only allows custom fields via `properties`)
 * so a frontend useFlag() check agrees with what the backend actually does
 * for the account being viewed.
 */
const UnleashAccountContext = () => {
  const accountUuid = useGetUuid();
  const email = useSelector(selectUserEmail);
  const updateContext = useUnleashContext();

  useEffect(() => {
    updateContext({
      properties: {
        ...(accountUuid ? { accountUuid } : undefined),
        ...(email ? { email } : undefined),
      },
    }).catch(() => undefined);
  }, [updateContext, accountUuid, email]);

  return null;
};
